import type { RefObject, ReactNode, MutableRefObject } from 'react';
import { startTransition } from 'react';

import { type CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';

import { checkSSRPartialSuccess } from '@confluence/ssr-utilities/entry-points/ssrPartialSuccess';

import type { LoaderResultType, LoadedModuleType, SSRSuccessCheckName } from '../types';
import { ActivationReason } from '../types';

export type UnlockReason =
	| 'ssrFailed'
	| 'hydrationDisabled'
	| 'hydrationFailed'
	| 'missingReactDOMPatch'
	| 'ssrCheckFailed'
	| 'immediateActivationCondition';

type UnlockReasonProps = Partial<Record<UnlockReason, boolean | undefined>>;

type UnlockReasonResult = {
	shouldUnlock: boolean;
	unlockReason: UnlockReason | undefined;
};

export type GateStatus = 'suspended' | 'loading' | 'resolved';

export type GateType = {
	promise: Promise<void>;
	resolve: () => void;
	getStatus: () => GateStatus;
	setLoading: () => void;
};

export type GateComponentProps<P> = {
	gate: GateType | null;
	id: string;
	loader?: () => LoaderResultType<P>;
	hostRef?: RefObject<HTMLDivElement | null>;
	onLoaded?: (m: LoadedModuleType<P>) => void;
	render: () => ReactNode;
	loaderPromiseRef?: MutableRefObject<Promise<void> | null>;
	ssrSuccessCheckName?: SSRSuccessCheckName;
	ssrSuccess?: boolean;
	activateComponent: (reason: ActivationReason, unlockReason?: UnlockReason) => void;
	immediateActivationCondition?: boolean;
};

export function makeGate(): GateType {
	let resolve!: () => void;
	let status: GateStatus = 'suspended';
	const promise = new Promise<void>((r) => (resolve = r));

	const resolveOnce = () => {
		if (status !== 'resolved') {
			status = 'resolved';
			resolve();
		}
	};

	const setLoading = () => {
		if (status === 'suspended') {
			status = 'loading';
		}
	};

	return {
		promise,
		resolve: resolveOnce,
		getStatus: () => status,
		setLoading,
	};
}

/**
 * Unlock immediately if hydration not possible or SSR failed. Reasons for unlocking:
 *
 * SSR failure
 * Hydration is not enabled
 * Hydration failed
 * React DOM is not patched correctly
 * SSR Check Failed (component's partial success is either not defined or failed)
 */
export function getUnlockReason(unlockReasons: UnlockReasonProps): UnlockReasonResult {
	const entry = Object.entries(unlockReasons).find(([, failure]) => failure);

	return {
		shouldUnlock: Boolean(entry),
		unlockReason: entry?.[0] as UnlockReason,
	};
}

export function unlockGate<P>(
	gate: GateType,
	id: string,
	loader: () => LoaderResultType<P>,
	hostRef: RefObject<HTMLDivElement | null>,
	onLoaded: (m: LoadedModuleType<P>) => void,
	loaderPromiseRef: MutableRefObject<Promise<void> | null>,
	createAnalyticsEvent: CreateUIAnalyticsEvent,
): Promise<void> {
	// Return existing promise if already loading
	if (loaderPromiseRef.current) {
		return loaderPromiseRef.current;
	}

	gate.setLoading?.();

	const promise = Promise.resolve()
		.then(() => loader())
		.then((mod) => {
			try {
				onLoaded(mod);
				if (typeof window !== 'undefined') {
					const host = hostRef?.current;
					gate.resolve();
					const root = window.__REACT_ROOT__;
					const target = host?.firstElementChild || host || undefined;
					root?.unstable_scheduleHydration?.(target);
				}
			} catch (error) {
				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						source: 'loadable',
						action: 'hydrationFailed',
						actionSubject: 'loadableComponent',
						attributes: {
							placeholderId: id,
							errorMessage: error instanceof Error ? error.message : 'Unknown error',
							errorStack: error instanceof Error ? error.stack : undefined,
						},
					},
				}).fire();
			}
		})
		.catch((error) => {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					source: 'loadable',
					action: 'loadFailed',
					actionSubject: 'loadableComponent',
					attributes: {
						placeholderId: id,
						errorMessage: error instanceof Error ? error.message : 'Unknown error',
						errorStack: error instanceof Error ? error.stack : undefined,
					},
				},
			}).fire();
		});

	loaderPromiseRef.current = promise;
	return promise;
}

export function Gate<P>({
	gate,
	render,
	ssrSuccessCheckName,
	ssrSuccess,
	activateComponent,
	immediateActivationCondition,
}: GateComponentProps<P>): ReactNode {
	//eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (!process.env.REACT_SSR && gate && gate.getStatus() !== 'resolved') {
		// Check SSR success: supports both global check (ssrSuccessCheckName) and per-instance (ssrSuccess)
		const ssrCheckFailed = ssrSuccessCheckName
			? !checkSSRPartialSuccess(ssrSuccessCheckName)
			: !ssrSuccess;

		// Unlock immediately if hydration not possible or SSR failed
		const { shouldUnlock, unlockReason } = getUnlockReason({
			ssrFailed: !window?.__SSR_RENDERED__,
			hydrationDisabled: !window?.__HYDRATABLE__,
			hydrationFailed: window?.__HYDRATION_FAILED__,
			missingReactDOMPatch: !window.__CC_REACT_DOM_CONTEXT_PATCH__,
			ssrCheckFailed,
		});

		if (shouldUnlock) {
			activateComponent(ActivationReason.IMMEDIATE_UNLOCK, unlockReason);
		} else if (immediateActivationCondition) {
			// unlocking safely relative to hydration, as attempting to hydrate
			// before gate is resolved causes hydration errors and unlocks all gates
			startTransition(() => {
				activateComponent(ActivationReason.IMMEDIATE_UNLOCK, 'immediateActivationCondition');
			});
		}
		// Keep the boundary suspended until the gate is resolved
		throw gate.promise;
	}
	return render();
}
